import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Select, DatePicker, Row, Col, message } from "antd";
import BaseModal from "../BaseModal";
import { fetchUsers } from "../../api/UsersApi";
import moment from "moment";

const DATE_FMT = "MM/DD/YYYY h:mm A";

// Step 2 of FsuUpdateStatus - matches the Figma "Resolve Order Conflicts" modal (node
// 7505:20923). Shown only when marking orders On Hand AND at least one selected order's pickup
// leg still needs manual completion (see PickupLegCandidatesApi - orders whose pickup is already
// COMPLETED, or that have already moved past their origin warehouse per the Linehaul system's
// own tracking, never reach this modal at all). Completes each pickup leg the same way manual
// completion does (BulkStatusUpdateService enqueues PickupLegCompletionWorker per order, which
// reuses ManualLocationLegCompletionService) - completion time is required per order, driver is
// optional (falls back to the warehouse's default driver). The bulk row is a front-end
// convenience that cascades into every candidate row; values submitted are still per-order.
//
// Vehicle/Trailer columns from the Figma design are intentionally omitted for now (no backend
// data source for a trailer picklist exists in this codebase - trailer_number is a free-text
// order field everywhere else - and the vehicle/fleet is auto-derived from the chosen driver by
// the reused completion service, not independently selectable yet).
function PickupCompletionModal(props) {
  const {
    visible, candidates, totalOrders, statusLabel, locationLabel, defaultDatetime,
    inProgress, onBack, onComplete, onCancel,
  } = props;

  const [drivers, setDrivers] = useState([]);
  const [fields, setFields] = useState({});
  const [staged, setStaged] = useState({
    driver_id: undefined,
    completed_at: defaultDatetime || moment(),
  });

  useEffect(() => {
    const initial = {};
    candidates.forEach((candidate) => {
      initial[candidate.customer_order_id] = {
        driver_id: undefined,
        completed_at: defaultDatetime || moment(),
      };
    });
    setFields(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  useEffect(() => {
    fetchUsers("driver")
      .then((result) => {
        if (result.success) {
          setDrivers(result.users || []);
        }
      })
      .catch(() => {});
  }, []);

  const setField = (orderId, field, value) => {
    setFields((prev) => ({ ...prev, [orderId]: { ...prev[orderId], [field]: value } }));
  };

  const applyStaged = () => {
    setFields((prev) => {
      const next = { ...prev };
      candidates.forEach((candidate) => {
        const orderId = candidate.customer_order_id;
        next[orderId] = {
          ...next[orderId],
          ...(staged.driver_id ? { driver_id: staged.driver_id } : {}),
          ...(staged.completed_at ? { completed_at: staged.completed_at } : {}),
        };
      });
      return next;
    });
  };

  const handleComplete = () => {
    const missing = candidates.filter((c) => {
      const field = fields[c.customer_order_id] || {};
      return !field.completed_at;
    });
    if (missing.length > 0) {
      message.error("Pickup Completion time is required for every order.");
      return;
    }

    // Driver is optional - orders left without one complete against the warehouse's default
    // driver, the same way ManualLocationLegCompletionService falls back for manual completion.
    const pickupCompletions = candidates.map((candidate) => {
      const field = fields[candidate.customer_order_id];
      return {
        customer_order_id: candidate.customer_order_id,
        pickup_driver_id: field.driver_id || null,
        pickup_completed_at: field.completed_at.toISOString(),
      };
    });
    onComplete(pickupCompletions);
  };

  const Footer = () => (
    <Row id="pickup-completion-footer-row" type="flex" justify="space-between" align="middle" style={{ width: "100%" }}>
      <Col id="pickup-completion-footer-back-col">
        <Button id="pickup-completion-back" data-testid="pickup-completion-back" onClick={onBack} disabled={inProgress}>
          Back
        </Button>
      </Col>
      <Col id="pickup-completion-footer-complete-col">
        <Button id="pickup-completion-complete" data-testid="pickup-completion-complete" type="primary" onClick={handleComplete} loading={inProgress}>
          Complete Pickup
        </Button>
      </Col>
    </Row>
  );

  // A plain array of Option elements (NOT wrapped in a Fragment) - antd's Select walks its
  // children directly and a <>...</> wrapper reads as a single childless node to it, which is
  // exactly what threw "Need at least a key or a value or a label" here.
  const driverOptions = drivers.map((driver) => (
    <Select.Option id={`fsu-driver-option-${driver.id}`} key={driver.id} value={driver.id}>{driver.full_name}</Select.Option>
  ));

  const ModalTitle = () => (
    <div id="pickup-completion-modal-title" data-testid="pickup-completion-modal-title">
      <div id="pickup-completion-heading" className="fsu-modal-heading">Resolve Order Conflicts</div>
      <div id="pickup-completion-subtitle" className="fsu-modal-subtitle">
        {`${candidates.length} of the ${totalOrders} orders you're marking ${statusLabel} at ${locationLabel} `}
        have conflicts to resolve in order to update their statuses.
      </div>
    </div>
  );

  return (
    <BaseModal
      id="pickup-completion-base-modal"
      title={<ModalTitle />}
      visible={visible}
      onCancel={onCancel}
      destroyOnClose
      width={660}
      maskClosable={false}
      footer={<Footer />}
    >
      <div id="pickup-completion-modal" data-testid="pickup-completion-modal">
        <div id="pickup-completion-section-title" className="fsu-section-title">Mark Pickup Completion</div>
        <div id="pickup-completion-section-subtitle" className="fsu-section-subtitle" style={{ marginBottom: 8 }}>
          These orders haven&apos;t completed pickup yet.
        </div>

        <div id="fsu-pickup-table" className="fsu-pickup-table">
          <Row id="fsu-pickup-table-header-row" gutter={12} align="middle" className="fsu-pickup-table-header">
            <Col id="pickup-completion-bulk-label-col" span={6}>{`Apply to ${candidates.length} Orders:`}</Col>
            <Col id="pickup-completion-bulk-datetime-col" span={8}>
              <DatePicker
                id="pickup-completion-bulk-datetime"
                data-testid="pickup-completion-bulk-datetime"
                value={staged.completed_at}
                onChange={(value) => setStaged((prev) => ({ ...prev, completed_at: value }))}
                showTime={{ use12Hours: true, format: "h:mm A" }}
                format={DATE_FMT}
                style={{ width: "100%" }}
              />
            </Col>
            <Col id="pickup-completion-bulk-driver-col" span={7}>
              <Select
                id="pickup-completion-bulk-driver"
                data-testid="pickup-completion-bulk-driver"
                placeholder="Select Driver (optional)"
                value={staged.driver_id}
                onChange={(value) => setStaged((prev) => ({ ...prev, driver_id: value }))}
                showSearch
                optionFilterProp="children"
                style={{ width: "100%" }}
              >
                {driverOptions}
              </Select>
            </Col>
            <Col id="pickup-completion-apply-col" span={3}>
              <Button id="pickup-completion-apply" data-testid="pickup-completion-apply" size="small" onClick={applyStaged} style={{ width: "100%" }}>
                Apply
              </Button>
            </Col>
          </Row>

          {candidates.map((candidate) => {
            const field = fields[candidate.customer_order_id] || {};
            return (
              <Row id={`pickup-completion-row-${candidate.customer_order_id}`} key={candidate.customer_order_id} gutter={12} align="middle" className="fsu-pickup-table-row">
                <Col id={`pickup-completion-order-col-${candidate.customer_order_id}`} span={6}>
                  <span id={`fsu-order-badge-${candidate.customer_order_id}`} className="fsu-order-badge">{candidate.order_type}</span>
                  <a
                    id={`fsu-order-link-${candidate.customer_order_id}`}
                    href={`/orders/${candidate.customer_order_id}/details`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fsu-order-link"
                  >
                    {candidate.order_number}
                  </a>
                </Col>
                <Col id={`pickup-completion-datetime-col-${candidate.customer_order_id}`} span={8}>
                  <DatePicker
                    id={`pickup-completion-datetime-${candidate.customer_order_id}`}
                    data-testid={`pickup-completion-datetime-${candidate.customer_order_id}`}
                    value={field.completed_at}
                    onChange={(value) => setField(candidate.customer_order_id, "completed_at", value)}
                    showTime={{ use12Hours: true, format: "h:mm A" }}
                    format={DATE_FMT}
                    disabled={inProgress}
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col id={`pickup-completion-driver-col-${candidate.customer_order_id}`} span={10}>
                  <Select
                    id={`pickup-completion-driver-${candidate.customer_order_id}`}
                    data-testid={`pickup-completion-driver-${candidate.customer_order_id}`}
                    placeholder="Select Driver (optional)"
                    value={field.driver_id}
                    onChange={(value) => setField(candidate.customer_order_id, "driver_id", value)}
                    showSearch
                    optionFilterProp="children"
                    disabled={inProgress}
                    style={{ width: "100%" }}
                  >
                    {driverOptions}
                  </Select>
                </Col>
              </Row>
            );
          })}
        </div>
      </div>
    </BaseModal>
  );
}

PickupCompletionModal.propTypes = {
  visible: PropTypes.bool,
  candidates: PropTypes.arrayOf(PropTypes.shape({
    customer_order_id: PropTypes.string,
    order_number: PropTypes.string,
    order_type: PropTypes.string,
  })).isRequired,
  totalOrders: PropTypes.number,
  statusLabel: PropTypes.string,
  locationLabel: PropTypes.string,
  defaultDatetime: PropTypes.object,
  inProgress: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PickupCompletionModal;
